import { useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import {
  Brain,
  Shield,
  BookOpen,
  Play,
  Menu,
  X,
  Mail,
  ChevronRight,
  Headphones,
  Calendar,
  Tag,
  User,
  MessageSquare,
  Mic } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Navigation items
const navItems = [
{ label: "Acasă", href: "#acasa" },
{ label: "Despre", href: "#despre" },
{ label: "Teme Principale", href: "#teme" },
{ label: "Autor", href: "#autor" },
{ label: "Ascultă", href: "#asculta" }];


// Platform data
const platforms = [
{
  name: "Spotify",
  url: "https://open.spotify.com/show/4DHBfjpLOSa4Psik1yNkeX",
  color: "#1DB954",
  icon: "spotify"
},
{
  name: "Apple Books",
  url: "https://books.apple.com/us/audiobook/r%C4%83zboiul-hibrid-si-consecin%C8%9Bele-lui-asupra-s%C4%83n%C4%83t%C4%83%C8%9Bii/id1833440897",
  color: "#000000",
  icon: "apple"
},
{
  name: "Audiobooks.com",
  url: "https://www.audiobooks.com/audiobook/9798295307935",
  color: "#F47920",
  icon: "audiobooks"
},
{
  name: "Libro.fm",
  url: "https://libro.fm/audiobooks/9798295307935",
  color: "#6B4C9A",
  icon: "libro"
},
{
  name: "Kobo",
  url: "https://www.kobo.com/us/en/audiobook/razboiul-hibrid-si-consecintele-lui-asupra-sanatatii-mentale-si-emotionale",
  color: "#000000",
  icon: "kobo"
}];


// Theme cards data
const themes = [
{
  title: "Războiul Hibrid și Mintea",
  description: "Conflictele moderne vizează din ce în ce mai mult percepția, emoția și credința, mai degrabă decât doar teritoriul.",
  image: "https://images.unsplash.com/photo-1764990189201-8025ff64d981?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtZW50YWwlMjByZXNpbGllbmNlJTIwZGFyayUyMGFic3RyYWN0fGVufDB8fHx8MTc3MTM5OTEzMHww&ixlib=rb-4.1.0&q=85"
},
{
  title: "Dezinformarea ca Stres Psihologic",
  description: "Narațiunile false și supraîncărcarea informațională erodează încrederea, claritatea și stabilitatea emoțională.",
  image: "https://images.unsplash.com/photo-1585900464046-f713a61424dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxjeWJlciUyMHdhcmZhcmUlMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxMzk5MTMyfDA&ixlib=rb-4.1.0&q=85"
},
{
  title: "Reziliența Mentală",
  description: "Ascultătorii învață cum să-și întărească apărările psihologice prin conștientizare și gândire critică.",
  image: "https://images.unsplash.com/photo-1767236798861-ba8b6372056a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxtZW50YWwlMjByZXNpbGllbmNlJTIwZGFyayUyMGFic3RyYWN0fGVufDB8fHx8MTc3MTM5OTEzMHww&ixlib=rb-4.1.0&q=85"
},
{
  title: "Adevărul ca Formă de Autoapărare",
  description: "Protejarea sănătății mentale începe cu capacitatea de a discerne adevărul de manipulare.",
  image: "https://images.unsplash.com/photo-1669052700037-db884b37b2d9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjeWJlciUyMHdhcmZhcmUlMjBhYnN0cmFjdCUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzcxMzk5MTMyfDA&ixlib=rb-4.1.0&q=85"
}];


// Feature cards data
const features = [
{
  icon: Brain,
  title: "Conștientizarea Tehnicilor Moderne",
  description: "Înțelegerea tehnicilor de război psihologic folosite în mediul actual de conflict hibrid."
},
{
  icon: Shield,
  title: "Stabilitate Emoțională",
  description: "Strategii practice pentru menținerea clarității mentale și a echilibrului emoțional."
},
{
  icon: BookOpen,
  title: "Instrumente de Reziliență Mentală",
  description: "Tehnici acționabile pentru construirea puterii psihologice pe termen lung."
}];


// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#acasa"
            onClick={(e) => scrollToSection(e, '#acasa')}
            className="text-[#d9fb06] font-bold text-lg tracking-tight"
            data-testid="logo">

            RAZBOI HIBRID
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navItems.map((item) =>
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => scrollToSection(e, item.href)}
              className="nav-link text-sm text-[#a1a1aa] hover:text-white transition-colors"
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>

                {item.label}
              </a>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            data-testid="mobile-menu-button">

            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen &&
        <nav className="md:hidden py-4 border-t border-white/10 animate-fade-in" data-testid="mobile-nav">
            {navItems.map((item) =>
          <a
            key={item.href}
            href={item.href}
            onClick={(e) => scrollToSection(e, item.href)}
            className="block py-3 text-[#a1a1aa] hover:text-white transition-colors"
            data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>

                {item.label}
              </a>
          )}
          </nav>
        }
      </div>
    </header>);

};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="acasa"
      className="hero-bg min-h-screen flex items-center pt-20"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1766438420966-7cfcde1a2389?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxtZW50YWwlMjByZXNpbGllbmNlJTIwZGFyayUyMGFic3RyYWN0fGVufDB8fHx8MTc3MTM5OTEzMHww&ixlib=rb-4.1.0&q=85')"
      }}
      data-testid="hero-section">

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
        <div className="max-w-4xl">
          {/* Published date */}
          <p className="text-[#d9fb06] text-sm tracking-wide uppercase mb-6 animate-fade-in-up">
            Publicat pe 25 Mai 2025
          </p>
          
          {/* Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
            Războiul Hibrid și Consecințele lui Asupra Sănătății Mentale și Emoționale
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl text-[#a1a1aa] mb-4 animate-fade-in-up animation-delay-200">
            Un îndemn la reziliență, claritate și adevăr într-o eră a dezinformării.
          </p>
          
          {/* Author */}
          <p className="text-[#a1a1aa] mb-10 animate-fade-in-up animation-delay-300">
            De <span className="text-white font-medium">Daniel A. Stoescu</span>
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
            <Button
              onClick={() => scrollToSection('#despre')}
              variant="outline"
              className="border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] font-bold px-8 py-6 rounded-full transition-all duration-300"
              data-testid="hero-learn-more-btn">

              <ChevronRight className="mr-2 h-5 w-5" />
              Află Mai Mult
            </Button>
            <Button
              onClick={() => scrollToSection('#asculta')}
              className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c5e605] font-bold px-8 py-6 rounded-full transition-all duration-300 btn-glow"
              data-testid="hero-listen-btn">

              <Play className="mr-2 h-5 w-5" />
              Ascultă Acum
            </Button>
          </div>
        </div>
      </div>
    </section>);

};

// About Section
const AboutSection = () => {
  return (
    <section id="despre" className="py-20 md:py-32 bg-[#1a1c1b]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-semibold text-white mb-6">
            Despre Audiobook
          </h2>
          <p className="text-[#a1a1aa] max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
            Acest audiobook explorează impactul psihologic al conflictului modern—în special expunerea constantă la dezinformare, manipulare emoțională și supraîncărcarea cognitivă. În loc să promoveze frica, cartea îi împuternicește pe ascultători cu conștientizare, strategii și instrumente pentru menținerea stabilității emoționale și construirea rezilienței mentale pe termen lung.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) =>
          <div
            key={index}
            className="feature-card bg-[#252827] p-8 rounded-xl border border-white/5"
            data-testid={`feature-card-${index}`}>

              <div className="w-14 h-14 bg-[#d9fb06]/10 rounded-lg flex items-center justify-center mb-6">
                <feature.icon className="w-7 h-7 text-[#d9fb06]" />
              </div>
              <h3 className="text-xl font-medium text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-[#a1a1aa] leading-relaxed">
                {feature.description}
              </p>
            </div>
          )}
        </div>

        {/* Callout */}
        <div className="bg-[#252827] border-l-4 border-[#d9fb06] p-8 rounded-r-xl">
          <p className="text-white text-lg italic">
            "În esență, această lucrare este un îndemn la acțiune: protejarea sănătății mentale nu mai este pasivă—necesită un efort conștient și informat."
          </p>
        </div>
      </div>
    </section>);

};

// Core Themes Section
const ThemesSection = () => {
  return (
    <section id="teme" className="py-20 md:py-32 bg-[#0f1110]" data-testid="themes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">
            Teme Principale
          </h2>
          <p className="text-[#a1a1aa] text-base md:text-lg">
            Concepte esențiale explorate în audiobook
          </p>
        </div>

        {/* Theme Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {themes.map((theme, index) =>
          <div
            key={index}
            className="group relative overflow-hidden rounded-2xl aspect-[16/10] cursor-pointer"
            data-testid={`theme-card-${index}`}>

              {/* Background Image */}
              <img
              src={theme.image}
              alt={theme.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />

              
              {/* Overlay */}
              <div className="absolute inset-0 theme-card-overlay" />
              
              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-medium text-white mb-2">
                  {theme.title}
                </h3>
                <p className="text-[#a1a1aa] text-sm md:text-base leading-relaxed">
                  {theme.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Author Section
const AuthorSection = () => {
  return (
    <section id="autor" className="py-20 md:py-32 bg-[#1a1c1b]" data-testid="author-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Author Image */}
          <div className="flex justify-center md:justify-start">
            <div className="relative">
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl bg-[#252827] border border-white/10 flex items-center justify-center overflow-hidden">
                <User className="w-24 h-24 text-[#52525b]" />
              </div>
              {/* Decorative accent */}
              <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-[#d9fb06]/30 rounded-2xl -z-10" />
            </div>
          </div>

          {/* Author Info */}
          <div>
            <p className="text-[#d9fb06] text-sm tracking-wide uppercase mb-4">
              Despre Autor
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
              Daniel A. Stoescu
            </h2>
            <div className="space-y-4 text-[#a1a1aa] leading-relaxed">
              <p>
                Daniel A. Stoescu este autorul lucrării Războiul Hibrid și Consecințele lui Asupra Sănătății Mentale și Emoționale. Munca sa se concentrează pe intersecția dintre psihologie, conflict, dezinformare și reziliența individuală.
              </p>
              <p>
                Prin acest audiobook, Stoescu îi încurajează pe ascultători nu doar să se protejeze, ci să devină participanți activi în promovarea adevărului și a bunăstării psihologice în societate.
              </p>
              <p>
                Abordarea sa combină rigoarea academică cu înțelepciunea practică, oferind instrumente care îi împuternicesc pe indivizi să navigheze complexitățile războiului informațional modern.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Listen Section
const ListenSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      toast.success("Mulțumim! Vă vom notifica când audiobook-ul devine disponibil pe alte platforme.");
      setEmail("");
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="asculta" className="py-20 md:py-32 bg-[#0f1110]" data-testid="listen-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">
            Ascultă Audiobook-ul
          </h2>
          <p className="text-[#a1a1aa] max-w-2xl mx-auto text-base md:text-lg">
            Disponibil acum pe mai multe platforme. Experimentează audiobook-ul complet și descoperă instrumente practice pentru reziliență mentală.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Info & Platforms */}
          <div>
            {/* Book Info */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-[#252827] p-4 rounded-xl border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <Mic className="w-5 h-5 text-[#d9fb06]" />
                  <span className="text-[#a1a1aa] text-sm">Format</span>
                </div>
                <p className="text-white font-medium">Audiobook narrat de AI</p>
              </div>
              <div className="bg-[#252827] p-4 rounded-xl border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#d9fb06]" />
                  <span className="text-[#a1a1aa] text-sm">Data Lansării</span>
                </div>
                <p className="text-white font-medium">10 August 2025</p>
              </div>
              <div className="bg-[#252827] p-4 rounded-xl border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <Tag className="w-5 h-5 text-[#d9fb06]" />
                  <span className="text-[#a1a1aa] text-sm">Genuri</span>
                </div>
                <p className="text-white font-medium text-sm">Dezvoltare Personală, Psihologie</p>
              </div>
              <div className="bg-[#252827] p-4 rounded-xl border border-white/5">
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-5 h-5 text-[#d9fb06]" />
                  <span className="text-[#a1a1aa] text-sm">Autor</span>
                </div>
                <p className="text-white font-medium">Daniel A. Stoescu</p>
              </div>
            </div>

            {/* Platform Buttons */}
            <div className="space-y-3">
              {platforms.map((platform, index) =>
              <a
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="platform-btn w-full flex items-center justify-between px-6 py-4 rounded-lg font-semibold text-white"
                style={{ backgroundColor: platform.color === "#000000" ? "#252827" : platform.color }}
                data-testid={`platform-${platform.name.toLowerCase().replace(/\s+/g, '-')}`}>

                  <div className="flex items-center gap-3">
                    <Headphones className="w-5 h-5" />
                    <span>{platform.name}</span>
                  </div>
                  <ChevronRight className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Right Column - Email Form */}
          <div className="flex items-center justify-center">
            <div className="bg-[#252827] p-8 rounded-2xl border border-white/5 w-full max-w-md">
              <h3 className="text-2xl font-semibold text-white mb-2">
                Ascultă Acum
              </h3>
              <p className="text-[#a1a1aa] mb-6">
                Primește notificări despre platforme noi
              </p>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="Introdu adresa de email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-[#1a1c1b] border-white/10 text-white placeholder:text-[#52525b] h-12 focus:border-[#d9fb06] focus:ring-[#d9fb06]"
                    data-testid="email-input" />

                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c5e605] font-bold py-6 rounded-lg transition-all duration-300"
                  data-testid="notify-btn">

                  {isSubmitting ? "Se trimite..." : "Notifică-mă"}
                </Button>
              </form>
              
              <p className="text-[#52525b] text-sm mt-4 text-center">
                Vă vom trimite actualizări când audiobook-ul devine disponibil pe platforme suplimentare
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Footer Component
const Footer = () => {
  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="py-16 bg-[#0f1110] border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h4 className="text-[#d9fb06] font-bold text-lg mb-4">
              Sănătate Mintală și Emoțională în Mediul Actual de Război Hibrid
            </h4>
            <p className="text-[#a1a1aa] text-sm">
              De Daniel A. Stoescu
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h5 className="text-white font-semibold mb-4">Link-uri Rapide</h5>
            <nav className="space-y-2">
              {navItems.map((item) =>
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollToSection(e, item.href)}
                className="block text-[#a1a1aa] hover:text-white transition-colors text-sm"
                data-testid={`footer-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>

                  {item.label}
                </a>
              )}
            </nav>
          </div>

          {/* Book Info */}
          <div>
            <h5 className="text-white font-semibold mb-4">Despre Carte</h5>
            <ul className="space-y-2 text-[#a1a1aa] text-sm">
              <li>Publicat pe 25 Mai 2025</li>
              <li>Audiobook narrat de AI</li>
              <li>Genuri: Dezvoltare Personală, Psihologie, Știință & Tehnologie</li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-white/5 text-center">
          <p className="text-[#52525b] text-sm">
            © 2025 Daniel A. Stoescu. Toate drepturile rezervate.
          </p>
        </div>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          style: {
            background: '#252827',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }} />

      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <ThemesSection />
        <AuthorSection />
        <ListenSection />
      </main>
      <Footer />
    </div>);

}

export default App;